import type { C1_Consumer } from "./C1_Consumer";
import type { ChannelType } from "../../../src/models/ChannelType";
import { C1_BaseObject } from "./C1_BaseObject";
import type { C1_FurnitureDefinition, C1_FurnitureProps, FurnitureProps } from "../types/C1_FurnitureTypes";
import type { C1_FurnitureData, FurniturePosition, WallSide } from "../types/C1_ModelTypes";
import type { C1_Room } from "./C1_Room";
import {
  isLightGroupDefinitionId,
  isSingleLightDefinitionId,
  syncLightGroup,
  syncSingleLight,
} from "../furniture/C1_lightGroup";
import { clampToRoomPolygon } from "../furniture/C1_roomPlacementGeometry";
import { resolveFurnitureWallDocking } from "../furniture/C1_furnitureDocking";
import ObjectFactory from "../../../src/models/ObjectFactory";
import type BaseModel from "../../../src/models/BaseModel";
import type Consumer from "../../../src/models/Consumer";
import Group from "../../../src/models/Group";
import { c1_state } from "./C1_runtimeContext";
import { replaceMaterializedEloDefinitionTree } from "./C1_materializeEloDefinition";
import { getMaterializedEloRoot } from "./C1_materializeEloDefinition";
import type { C1_Wall } from "./C1_Wall";
import type { C1_EloGroup } from "./C1_EloGroup";
import { getEloItemProfile } from "../data/C1_eloItemsRegistry";
import { addWallDockedEloDefaults, createEquipmentInstance } from "./C1_ensureEloEquipmentDefaults";
import { furnitureResolvers } from "../furniture/C1_furnitureResolvers";

export class C1_Furniture extends C1_BaseObject<C1_FurnitureData> {
  static setDisplayNameResolver(resolver: (definitionId: string) => string | undefined) {
    furnitureResolvers.displayName = resolver;
  }

  static setDefinitionResolver(resolver: (definitionId: string) => C1_FurnitureDefinition | undefined) {
    furnitureResolvers.definition = resolver;
  }

  get className(): string {
    return "C1_Furniture";
  }

  constructor(data: C1_FurnitureData, owner: Group | Consumer) {
    super(data, owner);
    if (!this._data._wallId && data.side) this._data._wallId = this.room?.wallForSide(data.side)?.id;
  }

  get definitionId() {
    return this.data.definitionId;
  }

  /** Resolves the runtime furniture definition used by the editor and navigator. */
  get definition(): C1_FurnitureDefinition | undefined {
    return furnitureResolvers.definition?.(this.definitionId);
  }

  get side() {
    return this.data.side;
  }

  get wallDocked() {
    return this.data.wallDocked;
  }

  get reflect() {
    return this.data.reflect;
  }

  get relativePosition() {
    return this.data.relativePosition;
  }

  /** Reads electrical properties from the existing consumer, including edits in the main app. */
  get baseProps() {
    const consumer = this.lightConsumer;
    if (!consumer) return this.data.baseProps;
    return { ...this.data.baseProps, channelType: consumer.channelType, isDali: consumer.isDali,
      ...(this.owner?.className === "Consumer" ? {
        lightProductId: consumer.lightProductId,
        effectiveRows: this.wallDocked !== undefined ? 1 : this.data.baseProps.rows,
      } : {}) };
  }

  get lightConsumer(): C1_Consumer | undefined {
    // A consumer-owned facade resolves through its owner, independently of the
    // palette/catalog key (which may be stale while a project is being loaded).
    if (this.owner?.className === "Consumer") {
      const facet = this.owner.c1;
      return facet?.className === "C1_Consumer" ? facet as C1_Consumer : undefined;
    }
    if (!isLightGroupDefinitionId(this.definitionId)) return undefined;
    const group = this.owner as Group | undefined;
    for (const consumer of group?.consumers ?? []) {
      if (consumer.c1?.className === "C1_Consumer") return consumer.c1 as C1_Consumer;
    }
    return undefined;
  }

  /** Changes the light function without rebuilding the group's device or its connections. */
  setLightChannelType(value: ChannelType): void { this.lightConsumer?.setChannelType(value); }

  setLightDali(value: boolean): void { this.lightConsumer?.setDali(value); }


  /** Transient compatibility tree derived exclusively from persisted ESTADA children. */
  get eloTree() {
    if (this.owner?.className === "Consumer") {
      const consumer = this.lightConsumer;
      if (!consumer) return undefined;
      const light = consumer.toLegacyTreeData();
      return { id: this.id, definitionId: consumer._defId.split("/")[0], items: [light] };
    }
    return getMaterializedEloRoot(this.owner as Group | undefined, this.definitionId)?.toLegacyTreeData();
  }

  /** Replaces the persisted ESTADA ELO children from an editor-produced transient tree. */
  setEloTree(eloTree: import("../types/C1_EloTypes").C1_EloItem_Data | undefined): void {
    this.beforeChange("eloItems");
    this.replaceEloTree(eloTree);
    this.changed("eloItems");
  }

  private replaceEloTree(tree: import("../types/C1_EloTypes").C1_EloItem_Data | undefined): void {
    if (this.owner?.className === "Consumer") {
      if (tree) this.lightConsumer?.updateLightGroupTree(tree);
    } else {
      replaceMaterializedEloDefinitionTree(this.owner as Group | undefined, tree);
    }
  }

  protected override changed(property?: string): void {
    if (this.owner?.className === "Consumer") this.lightConsumer?.setLightGroupFurniture(this.toJSON());
    super.changed(property);
  }

  override get masterIndex(): number | undefined {
    return this.owner?.className === "Consumer" ? this.lightConsumer?.masterIndex : super.masterIndex;
  }

  override setMasterIndex(masterIndex?: number): void {
    if (this.owner?.className === "Consumer") {
      // Tag numbering changes only the consumer's index, never layout or placement.
      this.lightConsumer?.setMasterIndex(masterIndex);
    } else {
      super.setMasterIndex(masterIndex);
    }
  }

  get absoluteRotation(): number {
    return Number(this.baseProps.rotation) || 0;
  }

  get absoluteReflect(): boolean {
    return this.reflect === true;
  }

  get room(): C1_Room | undefined {
    const group = this.owner as Group | undefined;
    const roomFacet = group?.parentRoom?.c1;
    return roomFacet?.className === "C1_Room" ? (roomFacet as C1_Room) : undefined;
  }

  /** Wall used for geometric docking; the facet persists only its generated ESTADA ID. */
  get dockWall(): C1_Wall | undefined {
    return state.objectRegistry.getObject(this.data._wallId ?? "") as C1_Wall | undefined;
  }

  set dockWall(wall: C1_Wall | undefined) {
    const wallId = wall?.id ?? "";
    if (wallId === (this.data._wallId ?? "")) return;
    this.beforeChange("wallId");
    this._data._wallId = wallId;
    this.changed("wallId");
  }

  equipmentPostitionGeometric(id: string): { x: number; y: number } | undefined {
    if (id !== this.id) return undefined;
    if (this.owner?.className === "Consumer") return this.lightConsumer?.independentAbsolutePosition;

    const room = this.room;
    const project = c1_state.project;
    if (!room || !project) return undefined;

    const furnitureRect = room.innerRect();

    return {
      x: furnitureRect.x1 + this.relativePosition.x * (furnitureRect.x2 - furnitureRect.x1),
      y: furnitureRect.y1 + this.relativePosition.y * (furnitureRect.y2 - furnitureRect.y1),
    };
  }

  setPosition(
    relativePosition: FurniturePosition,
    side?: WallSide,
    reflect = this.data.reflect,
    wallDocked?: "L" | "R" | "M",
    wallId?: string,
  ) {
    const wasWallDocked = this.wallDocked !== undefined;
    this.beforeChange("relativePosition");
    this._data.relativePosition = { ...relativePosition };
    this._data.side = side;
    this._data._wallId = side ? wallId ?? this.room?.wallForSide(side)?.id : undefined;
    this._data.reflect = reflect;
    this._data.wallDocked = wallDocked;
    this.syncWallDockedEloItems(!wasWallDocked && wallDocked !== undefined);
    if (isLightGroupDefinitionId(this.definitionId)) {
      const synchronized = syncLightGroup(this.baseProps, this.eloTree, wallDocked !== undefined, this.definitionId);
      this._data.baseProps = synchronized.props;
      this.replaceEloTree(synchronized.eloTree);
    } else if (isSingleLightDefinitionId(this.definitionId)) {
      const synchronized = syncSingleLight(this._data.baseProps, this.eloTree, wallDocked !== undefined, this.definitionId);
      this._data.baseProps = synchronized.props;
      this.replaceEloTree(synchronized.eloTree);
    }
    this.changed("relativePosition");
  }

  /**
   * Repositions this furniture at a point in its current room, redocking to the nearest wall only if the
   * point lands close enough to one. Used after the navigator moves this furniture into a different room,
   * where there is no drag pointer position to anchor the new placement (unlike dragging within the plan).
   */
  moveTo(point: FurniturePosition): void {
    const room = this.room;
    if (!room) return;
    const rect = room.innerRect();
    const width = rect.x2 - rect.x1;
    const height = rect.y2 - rect.y1;
    if (width <= 0 || height <= 0) return;
    const geometry = room.placementGeometry();
    const clamped = clampToRoomPolygon(point, geometry);
    const docking = resolveFurnitureWallDocking(clamped, geometry, {
      wallDockable: this.wallDocked !== undefined,
      autoRotate: this.wallDocked !== undefined,
      autoReflect: false,
      currentReflect: this.reflect,
    });
    this.setPosition(
      { x: (docking.position.x - rect.x1) / width, y: (docking.position.y - rect.y1) / height },
      docking.placement?.side,
      this.reflect,
      docking.placement?.wallDocked,
      docking.placement?.wallId,
    );
    if (docking.placement?.rotation !== undefined) this.setBaseProps({ ...this.baseProps, rotation: docking.placement.rotation });
  }

  /** Deletes through native models so undocking also removes wiring and supports undo. */
  syncWallDockedEloItems(addDefaults = false): void {
    if (this.owner?.className === "Consumer") return;
    const owner = this.owner as Group | undefined;
    if (!owner) return;
    if (this.wallDocked === undefined) {
      const removeDockedItems = (container: Group) => {
        for (const item of [...container.groups.asArray(), ...container.consumers.asArray(), ...container.buttons.asArray()]) {
          const facet = item.c1;
          if (!facet || !["C1_EloGroup", "C1_Consumer", "C1_Button"].includes(facet.className)) continue;
          if ((facet as C1_EloGroup).definition?.definitionProperties.onlyIfWallDocked) {
            item.delete();
          } else if (facet.className === "C1_EloGroup") {
            removeDockedItems(item as Group);
          }
        }
      };
      removeDockedItems(owner);
    } else if (addDefaults) {
      const project = c1_state.project;
      const tree = this.eloTree ?? createEquipmentInstance(this.definitionId, project.equipmentStandard, [], project.settings.buttonDefaults, true);
      const next = addWallDockedEloDefaults(tree, getEloItemProfile(this.definitionId), project.equipmentStandard, project.settings.buttonDefaults);
      if (next !== tree) this.setEloTree(next);
    }
  }

  setReflect(reflect: boolean) {
    if (this.data.reflect === reflect) return;
    this.beforeChange("reflect");
    this._data.reflect = reflect;
    this.changed("reflect");
  }

  setBaseProps(baseProps: FurnitureProps) {
    const disabledProperties = new Set(
      Object.keys(this.baseProps).filter(key => this.baseProps[key] === true && baseProps[key] !== true),
    );
    this.beforeChange("baseProps");
    if (isLightGroupDefinitionId(this.definitionId)) {
      const synchronized = syncLightGroup(baseProps, this.eloTree, this.wallDocked !== undefined, this.definitionId);
      this._data.baseProps = synchronized.props;
      this.replaceEloTree(synchronized.eloTree);
    } else if (isSingleLightDefinitionId(this.definitionId)) {
      const synchronized = syncSingleLight(baseProps, this.eloTree, this.wallDocked !== undefined, this.definitionId);
      this._data.baseProps = synchronized.props;
      this.replaceEloTree(synchronized.eloTree);
    } else {
      this._data.baseProps = { ...baseProps } as C1_FurnitureProps;
    }

    if (disabledProperties.size) {
      const removeDisabledGroups = (container: Group) => {
        for (const group of container.groups.asArray()) {
          if (group.c1?.className !== "C1_EloGroup") continue;
          const requirement = (group.c1 as C1_EloGroup).definition?.definitionProperties.requiresFurnitureProperty;
          if (requirement && disabledProperties.has(requirement)) {
            // Use native deletion so descendants, connections and undo stay consistent.
            group.delete();
          } else {
            removeDisabledGroups(group);
          }
        }
      };
      if (this.owner && this.owner.className !== "Consumer") removeDisabledGroups(this.owner as Group);
    }

    this.changed("baseProps");
  }

  /** Electrical function values are persisted on the consumer only. */
  private persistentBaseProps(): FurnitureProps {
    if (!this.lightConsumer) return { ...this.data.baseProps };
    const { channelType, isDali, lightType, ...props } = this.data.baseProps;
    return props;
  }

  toJSON(): C1_FurnitureData {
    return {
      ...this.data,
      relativePosition: { ...this.data.relativePosition },
      baseProps: this.persistentBaseProps(),
    };
  }
}


ObjectFactory.registerClass(
  "c1:C1_Furniture",
  (data, owner) => new C1_Furniture(data, owner as Group) as unknown as BaseModel,
);
